import { BodyCell } from '@tableau/widgets-datagrid';
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import { FieldDataType } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createIconCellFormatter,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  TypedBodyCellProps,
  UnsortableColumnKey,
  UsualDisplayGridProps
} from '../../../data-catalog-shared';
import { createQueryPreservingReplacingLinkCellFormatter } from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { FieldIcon } from '../field/types/FieldIcon';
import { DescriptionIconFromRowData, DescriptionTextFromRowData, RowDataType } from '../fieldColumn/DescriptionInfo';
import { getNameWithApplicableDataType } from '../sharedDisplayGrid/FieldsDisplayGrid';
import { FieldsValidSortFields } from '../sorting/sortProviders/FieldsGridSortProvider';

export type LensFieldsDisplayGridRowData = Readonly<{
  lensId: string;
  name: string | null;
  dataType: FieldDataType | null;
  id: string;
  description?: string | null;
  fieldIcon: FieldIcon | null;
}>;

type LensFieldsColumnFactory = ColumnFactory<
  GridSortProps<FieldsValidSortFields>,
  LensFieldsDisplayGridRowData,
  FieldsValidSortFields
>;

const columns: ReadonlyArray<LensFieldsColumnFactory> = [
  (sortParams) => ({
    name: 'type',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderType(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createIconCellFormatter(
      ({ fieldIcon }) => <SvgFieldIcon fieldIcon={fieldIcon} />,
      ({ fieldIcon }) => fieldIcon ? FieldNameMappings[fieldIcon]() : ''
    ),
    sortable: false,
    resizable: false
  }),
  (sortParams) => ({
    name: 'name',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderName(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createQueryPreservingReplacingLinkCellFormatter<LensFieldsDisplayGridRowData>(
      getNameWithApplicableDataType,
      (urls, { id, lensId }) => urls.lensLineageFieldPath(lensId, id),
    ),
    sortable: false,
  }),
  (sortParams) => ({
    name: 'description',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderDescription(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: descriptionCellFormatter(),
    sortable: false,
    growToFit: true,
  }),
];

export type LensFieldsDisplayGridProps =
  UsualDisplayGridProps<LensFieldsDisplayGridRowData, GraphQLError> &
  GridSortProps<FieldsValidSortFields>;

export function LensFieldsDisplayGrid(props: LensFieldsDisplayGridProps): JSX.Element {
  const cols = columns.map(columnFactory => columnFactory(props));
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

// Following functions adapted from DatasourceFieldsDisplayGrid.tsx:

function descriptionCellFormatter(): React.Factory<BodyCell.Props> & React.Factory<TypedBodyCellProps<LensFieldsDisplayGridRowData>> {
  return createTextCellFormatter(
    descriptionTextFromRowData, // getTextFromRowData
    Messages.NoDescriptionGridPlaceholder(), // placeholder
    () => false, // getDisabledFromRowData (always enabled)
    false, // noPadding
    descriptionIconFromRowData, // getPostIconFromRowData
  );
}

function minifyRowData(rowData: LensFieldsDisplayGridRowData): RowDataType {
  return {
    description: rowData.description,
  };
}

function descriptionTextFromRowData(rowData: LensFieldsDisplayGridRowData) {
  return DescriptionTextFromRowData(minifyRowData(rowData));
}

function descriptionIconFromRowData(rowData: LensFieldsDisplayGridRowData) {
  return DescriptionIconFromRowData(minifyRowData(rowData));
}
